import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useContentOverrides } from '../../lib/useContentOverrides';

const API = process.env.REACT_APP_BACKEND_URL;

/*
 * Cinematic hero video carousel — wanderlust & escapism.
 * 5 curated clips: Greece coast, Tuscany vineyards, Phi Phi Islands, Rock of Cashel Ireland, Swiss Alps valley.
 * Continuous slow crossfade, no controls, no indicators.
 * Each clip plays ~12 seconds. Transition: 1.5s soft opacity crossfade.
 */
// Hero video lineup.  Files are locally re-encoded to HD 720p H.264 (~4-8 MB each) and served
// from our own backend, eliminating both Pexels CDN latency and the multi-100MB downloads
// that caused black flashes and stalls.  Each video has a matching first-frame poster JPG
// so the user sees a real photo immediately — never a black void — while bytes stream in.
const HERO_VIDEOS = [
  { src: `${API}/api/static/videos/v1.mp4`, poster: `${API}/api/static/posters/v1.jpg`, label: 'Greece coastal bay' },
  { src: `${API}/api/static/videos/v2.mp4`, poster: `${API}/api/static/posters/v2.jpg`, label: 'Tuscany vineyards' },
  { src: `${API}/api/static/videos/v3.mp4`, poster: `${API}/api/static/posters/v3.jpg`, label: 'Phi Phi Islands' },
  { src: `${API}/api/static/videos/v4.mp4`, poster: `${API}/api/static/posters/v4.jpg`, label: 'Ireland coast' },
  { src: `${API}/api/static/videos/v5.mp4`, poster: `${API}/api/static/posters/v5.jpg`, label: 'Swiss Alps' },
];

const TESTIMONIALS = [
  'They didn\u2019t just plan a trip. They understood what we needed before we could fully express it. Every detail felt intentional.',
  'The level of care and precision was unlike anything we\u2019ve experienced. It felt less like a vacation and more like a homecoming.',
  'They created space for us to actually be present. No noise, no rushing \u2014 just the experience itself.',
];

const VIDEO_DURATION = 12000;   // 12 seconds per clip
const FADE_DURATION  = 1500;    // 1.5s crossfade

// Regions featured on the homepage carousel deck — order matters and matches /destinations.
const FEATURED_REGIONS = ['Europe', 'Asia & Pacific', 'Africa & Indian Ocean'];

const REGION_HOLD_MS = 15000;     // each slide lingers 15s before crossfading
const REGION_FADE_MS = 3500;      // image + label crossfade duration

/*
 * RegionCard — homepage version of a destination card.  Crossfades through a shuffled pool of
 * {image, label} pairs drawn from every destination in the region.  Label fades in step with
 * the image so the city name underneath is always accurate to what is shown.
 */
function RegionCard({ region, anchor, slides }) {
  const [idx, setIdx] = useState(0);
  const [transitionKey, setTransitionKey] = useState(0);
  const [textVisible, setTextVisible] = useState(true);
  const count = slides.length;

  useEffect(() => {
    if (count <= 1) return;
    const timer = setInterval(() => {
      setTextVisible(false);
      setTimeout(() => {
        setIdx(prev => (prev + 1) % count);
        setTransitionKey(k => k + 1);
        setTextVisible(true);
      }, REGION_FADE_MS / 2);
    }, REGION_HOLD_MS);
    return () => clearInterval(timer);
  }, [count]);

  // Preload every slide so transitions never wait on network
  useEffect(() => {
    slides.forEach(s => { const img = new Image(); img.src = s.image; });
  }, [slides]);

  const cur = count > 0 ? slides[idx % count] : null;
  const prev = count > 0 ? slides[(idx - 1 + count) % count] : null;

  return (
    <Link
      to={`/destinations#${anchor}`}
      className="group block"
      data-testid={`home-region-${anchor}`}
    >
      {cur ? (
        <div className="aspect-[4/3] rounded mb-4 overflow-hidden bg-[#1A1A1A] relative">
          {/* Bottom layer — previous image held at full opacity so the page bg never bleeds
              through during a fade. */}
          <img
            key={`bg-${idx}`}
            src={prev.image}
            alt=""
            aria-hidden="true"
            className="absolute inset-0 w-full h-full object-cover"
            decoding="async"
          />
          {/* Top layer — current image, re-mounted each tick so the keyframe fade-in re-runs */}
          <img
            key={`fg-${transitionKey}`}
            src={cur.image}
            alt={cur.label}
            className="absolute inset-0 w-full h-full object-cover group-hover:scale-105"
            style={{
              animation: `verault-dest-fadein ${REGION_FADE_MS}ms ease-in-out forwards`,
              transition: 'transform 700ms ease-out',
            }}
            decoding="async"
          />
        </div>
      ) : (
        <div className="aspect-[4/3] bg-[#F0EDE6] rounded mb-4 group-hover:bg-[#E8E4DC] transition-colors" />
      )}
      <p className="font-serif text-base text-[#1A1A1A] mb-1 group-hover:underline">{region}</p>
      <p className="text-xs text-[#999] h-4" data-testid={`home-region-${anchor}-label`}>
        <span
          className="inline-block"
          style={{
            opacity: textVisible ? 1 : 0,
            transition: `opacity ${REGION_FADE_MS / 2}ms ease-in-out`,
          }}
        >
          {cur?.label || 'Coming soon'}
        </span>
      </p>
    </Link>
  );
}

export default function HomePage() {
  const c = useContentOverrides('home');
  /* --- Video Carousel ---
   *
   * Performance design:
   *   1. Videos are locally re-encoded to HD 720p H.264 (~4-8 MB each, 31 MB total — was
   *      257 MB on the Pexels CDN) and served from our backend with HTTP range support, so
   *      every clip is fully bufferable within a couple of seconds even on mobile data.
   *   2. Every video carries a `poster` JPG (the clip's first frame).  The poster paints
   *      INSTANTLY on page load, eliminating the black void users used to see while the
   *      video downloaded — they always see a real travel photograph.
   *   3. All videos render with preload="auto" so the browser starts downloading every clip
   *      in parallel as soon as the page mounts.  Combined with our fetch() warm-up below,
   *      this guarantees that future transitions never wait on network.
   *   4. Transitions wait for readyState === 4 (HAVE_ENOUGH_DATA) before swapping, with a
   *      10 s fallback so we never freeze indefinitely.
   *   5. Previous video keeps playing through and after the fade (keeps the decoder warm).
   */
  const videoRefs = useRef([]);
  const activeRef = useRef(0);
  const [activeIdx, setActiveIdx] = useState(0);

  useEffect(() => {
    if (HERO_VIDEOS.length === 0) return;

    // Preload every poster JPG into the browser cache so swapping the backdrop layer during
    // a transition paints instantly — no fade-in-from-nothing on the still image.
    HERO_VIDEOS.forEach(v => { const img = new Image(); img.src = v.poster; });

    // Kick off every clip's download in parallel via .load() — preload="auto" already does
    // this, but calling load() explicitly avoids any race with React's render lifecycle.
    videoRefs.current.forEach(el => { if (el) { try { el.load(); } catch (_) {} } });

    // Belt-and-suspenders preloader: some mobile browsers ignore preload="auto" for data
    // savings.  We force the bytes into the HTTP cache by fetching each clip ourselves in
    // the background so the <video> element's eventual .play() resolves instantly from cache.
    const controller = new AbortController();
    HERO_VIDEOS.forEach((v, i) => {
      if (i === 0) return; // first is already actively decoding via .play()
      fetch(v.src, { signal: controller.signal, cache: 'force-cache' })
        .then(r => r.blob())
        .catch(() => {});
    });

    const first = videoRefs.current[0];
    if (first) first.play().catch(() => {});

    if (HERO_VIDEOS.length < 2) return () => controller.abort();

    const timer = setInterval(() => {
      const prev = activeRef.current;
      const next = (prev + 1) % HERO_VIDEOS.length;
      const nextEl = videoRefs.current[next];
      if (!nextEl) return;

      const doTransition = () => {
        nextEl.currentTime = 0;
        nextEl.play().catch(() => {});
        activeRef.current = next;
        setActiveIdx(next);
        // Intentionally do NOT pause the previous video — keeps its decoder warm and avoids
        // a stutter if the user scrolls back into the hero shortly after a transition.
      };

      if (nextEl.readyState >= 4) {
        doTransition();
      } else {
        const onReady = () => {
          nextEl.removeEventListener('canplaythrough', onReady);
          doTransition();
        };
        nextEl.addEventListener('canplaythrough', onReady);
        setTimeout(() => {
          nextEl.removeEventListener('canplaythrough', onReady);
          if (activeRef.current === prev) doTransition();
        }, 10000);
      }
    }, VIDEO_DURATION);

    return () => {
      clearInterval(timer);
      controller.abort();
    };
  }, []);

  /* --- Testimonial Rotation --- */
  const [quoteIdx, setQuoteIdx] = useState(0);
  const [quoteVisible, setQuoteVisible] = useState(true);
  // Testimonials can be edited from the studio; the canonical fallback
  // ships in the bundle so the site never goes blank.
  const testimonialQuotes = (c.get('testimonial_quotes', null) || []).map(q => q?.text).filter(Boolean);
  const testimonials = testimonialQuotes.length ? testimonialQuotes : TESTIMONIALS;

  useEffect(() => {
    const timer = setInterval(() => {
      setQuoteVisible(false);
      setTimeout(() => {
        setQuoteIdx(prev => (prev + 1) % testimonials.length);
        setQuoteVisible(true);
      }, 800);
    }, 7000);
    return () => clearInterval(timer);
  }, [testimonials.length]);

  /* --- Featured Destinations (deep-linked to /destinations) --- */
  // For each region, build a pool of {image, label} pairs by zipping each destination's
  // carousel_images with the matching city names from its short_description.  Shuffle them so
  // the homepage feels alive on each load.  The RegionCard component below crossfades through
  // these slides exactly like the destinations page — image and label are always in sync.
  const [destinations, setDestinations] = useState([]);

  useEffect(() => {
    axios.get(`${API}/api/cms/destinations/published`)
      .then(res => setDestinations(res.data || []))
      .catch(() => {});
  }, []);

  const regionCards = useMemo(() => {
    const byRegion = {};
    destinations.forEach(d => {
      if (!byRegion[d.region]) byRegion[d.region] = [];
      byRegion[d.region].push(d);
    });
    return FEATURED_REGIONS.map(region => {
      const dests = byRegion[region] || [];
      const pool = [];
      dests.forEach(d => {
        const cities = (d.short_description || '').split(',').map(s => s.trim()).filter(Boolean);
        const images = (d.carousel_images && d.carousel_images.length)
          ? d.carousel_images
          : (d.external_hero_url ? [d.external_hero_url] : []);
        const n = Math.min(cities.length, images.length);
        for (let i = 0; i < n; i++) {
          // "City, Country" reads naturally beneath the image and is unambiguous
          pool.push({ image: images[i], label: `${cities[i]}, ${d.destination_name}` });
        }
      });
      // Fisher-Yates shuffle so the slide order varies between page loads
      for (let i = pool.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [pool[i], pool[j]] = [pool[j], pool[i]];
      }
      const anchor = region.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
      return { region, anchor, slides: pool };
    });
  }, [destinations]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div data-testid="page-home">
      {c.previewMode && (
        <div className="bg-[#1A1A1A] text-white text-center py-2 px-4 text-[11px] tracking-[0.22em] uppercase font-mono" data-testid="home-preview-banner">
          Studio draft preview &mdash; not yet published
        </div>
      )}
      {/* Hero Section — Cinematic Video Carousel */}
      <section
        className="relative min-h-[100vh] flex items-center justify-center overflow-hidden"
        style={{
          background:
            'radial-gradient(circle at 50% 40%, #2a2a2a 0%, #1A1A1A 60%, #0d0d0d 100%)',
        }}
        data-testid="home-hero"
      >
        {/* Permanent poster backdrop — full-resolution still photo for whichever clip is
            currently active.  Always rendered at full opacity so the user NEVER sees a black
            screen, no matter how slow the video is to buffer.  We just swap its src when the
            active video changes — the browser paints the new photo before the new video has
            its first decoded frame, so transitions stay seamless. */}
        <img
          src={HERO_VIDEOS[activeIdx].poster}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover"
          style={{ filter: 'brightness(1.12)' }}
          decoding="async"
        />
        {/* Stacked Videos — GPU-accelerated crossfade.  Each video fades in OVER the poster
            backdrop above, so the worst-case visual is "real travel photo", never black. */}
        {HERO_VIDEOS.map((v, i) => (
          <video
            key={v.src}
            ref={el => { videoRefs.current[i] = el; }}
            muted
            loop
            playsInline
            preload="auto"
            poster={v.poster}
            className="absolute inset-0 w-full h-full object-cover"
            style={{
              opacity: i === activeIdx ? 1 : 0,
              transition: `opacity ${FADE_DURATION}ms ease-in-out`,
              willChange: 'opacity',
              filter: 'brightness(1.12)',
            }}
            data-testid={`home-hero-video-${i}`}
          >
            <source src={v.src} type="video/mp4" />
          </video>
        ))}

        {/* Light overlay — 3% opacity: let colors pop, invoke wanderlust */}
        <div className="absolute inset-0 bg-black/[0.03]" />
        {/* Very subtle warm bottom gradient for cohesion, not weight */}
        <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, transparent 0%, rgba(26,26,26,0.15) 100%)' }} />

        {/* Frosted Glass Backdrop — lighter, more breathable */}
        <div className="relative z-10 px-6 flex justify-center">
          <div
            className="text-center max-w-2xl rounded-2xl"
            style={{
              background: 'rgba(255,255,255,0.04)',
              backdropFilter: 'blur(10px)',
              WebkitBackdropFilter: 'blur(10px)',
              border: '1px solid rgba(255,255,255,0.10)',
              boxShadow: '0 8px 32px rgba(0,0,0,0.06)',
              padding: '3rem 3.5rem',
            }}
            data-testid="home-hero-glass"
          >
            <p className="text-xs uppercase tracking-[0.15em] text-white/70 mb-6">{c.get('hero.kicker', 'Luxury Travel Design')}</p>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-white leading-tight">
              {c.get('hero.title_line_1', 'Travel')}<br />{c.get('hero.title_line_2', 'Designed with Intention')}
            </h1>
            <p className="mt-8 text-base text-white/80 max-w-xl mx-auto leading-relaxed">
              <span className="sm:block">{c.get('hero.subtitle_lead', 'Bespoke itineraries')}</span>
              <span> {c.get('hero.subtitle_tail', 'crafted with precision and care \u2014 for those who move with intention.')}</span>
            </p>
            <div className="mt-10 flex flex-col sm:flex-row justify-center gap-4">
              <Link
                to="/plan"
                className="px-8 py-3.5 text-white text-sm rounded-lg hover:bg-white/10 transition-colors"
                style={{
                  background: 'rgba(255,255,255,0.08)',
                  backdropFilter: 'blur(8px)',
                  WebkitBackdropFilter: 'blur(8px)',
                  border: '1px solid rgba(255,255,255,0.25)',
                }}
                data-testid="home-cta-plan"
              >
                {c.get('hero.cta_primary', 'Start the Conversation')}
              </Link>
              <Link
                to="/destinations"
                className="px-8 py-3.5 text-white/80 text-sm rounded-lg hover:bg-white/10 transition-colors"
                style={{
                  background: 'rgba(255,255,255,0.04)',
                  backdropFilter: 'blur(8px)',
                  WebkitBackdropFilter: 'blur(8px)',
                  border: '1px solid rgba(255,255,255,0.15)',
                }}
                data-testid="home-cta-destinations"
              >
                {c.get('hero.cta_secondary', 'Explore Destinations')}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Destinations — pulls live images from the same data source as /destinations */}
      <section className="py-16 sm:py-24 px-6 bg-white border-t border-[#E8E4DC]" data-testid="home-featured-destinations">
        <div className="max-w-6xl mx-auto">
          <div className="text-center max-w-2xl mx-auto mb-12 sm:mb-14">
            <p className="text-[10px] uppercase tracking-[0.28em] text-[#C9A961] mb-4">{c.get('featured_destinations.kicker', 'Curated Destinations')}</p>
            <h2 className="font-serif text-2xl sm:text-3xl text-[#1A1A1A] mb-3 leading-snug">{c.get('featured_destinations.title', 'Where We Travel')}</h2>
            <div className="w-10 h-px bg-[#E8E4DC] mx-auto" aria-hidden="true" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {regionCards.map(card => (
              <RegionCard
                key={card.region}
                region={card.region}
                anchor={card.anchor}
                slides={card.slides}
              />
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 sm:py-24 px-6 border-t border-[#E8E4DC]" data-testid="home-how-it-works">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12 sm:mb-14">
            <p className="text-[10px] uppercase tracking-[0.28em] text-[#C9A961] mb-4">{c.get('process.kicker', 'Process')}</p>
            <h2 className="font-serif text-2xl sm:text-3xl text-[#1A1A1A] mb-3 leading-snug">{c.get('process.title', 'How We Work')}</h2>
            <div className="w-10 h-px bg-[#E8E4DC] mx-auto" aria-hidden="true" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-14">
            {(c.get('process_steps', [
              { step: '01', title: 'Discovery', desc: 'We learn your travel style, preferences, and aspirations through a personal consultation.' },
              { step: '02', title: 'Design', desc: 'Your advisor crafts a bespoke itinerary tailored to who you are and who you are becoming.' },
              { step: '03', title: 'Experience', desc: 'Travel with confidence, supported every step of the way with precision and care.' },
            ])).map((item, ii) => (
              <div key={item.step || ii} className="text-left">
                <p className="text-[11px] text-[#C9A961] tracking-[0.18em] font-medium mb-3">{item.step}</p>
                <p className="font-serif text-lg text-[#1A1A1A] mb-3">{item.title}</p>
                <p className="text-sm text-[#6B6B6B] leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonial */}
      <section className="py-20 sm:py-24 px-6 bg-[#1A1A1A] text-white" data-testid="home-testimonial">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-[10px] uppercase tracking-[0.28em] text-[#C9A961] mb-8">{c.get('testimonials.kicker', 'Client Voices')}</p>
          <blockquote
            className="font-accent text-xl sm:text-2xl italic leading-relaxed text-white/90 transition-opacity duration-700"
            style={{ opacity: quoteVisible ? 1 : 0 }}
            data-testid="home-testimonial-quote"
          >
            &ldquo;{testimonials[quoteIdx % testimonials.length]}&rdquo;
          </blockquote>
        </div>
      </section>

      {/* Philosophy Teaser */}
      <section className="py-16 sm:py-24 px-6 border-t border-[#E8E4DC]" data-testid="home-philosophy">
        <div className="max-w-2xl mx-auto text-center">
          <p className="text-[10px] uppercase tracking-[0.28em] text-[#C9A961] mb-6">{c.get('philosophy_teaser.kicker', 'Our Philosophy')}</p>
          <p className="font-accent text-lg sm:text-xl text-[#1A1A1A] italic leading-relaxed mb-8">
            {c.get('philosophy_teaser.quote', 'This is not about seeing more. It is about feeling more \u2014 with precision.')}
          </p>
          <Link
            to="/about"
            className="text-sm text-[#6B6B6B] hover:text-[#1A1A1A] transition-colors underline underline-offset-4"
            data-testid="home-philosophy-link"
          >
            {c.get('philosophy_teaser.link_label', 'Read more')}
          </Link>
        </div>
      </section>

      {/* CTA — Filled Black */}
      <section className="py-16 sm:py-24 px-6 text-center bg-[#F5F3EE] border-t border-[#E8E4DC]" data-testid="home-cta">
        <p className="text-[10px] uppercase tracking-[0.28em] text-[#C9A961] mb-6">{c.get('cta.kicker', 'The Invitation')}</p>
        <p className="font-accent text-base sm:text-lg text-[#1A1A1A] italic mb-10">
          {c.get('cta.message', 'Ready to begin?')}
        </p>
        <Link
          to="/plan"
          className="px-8 py-3.5 bg-[#1A1A1A] text-white text-sm tracking-wide rounded hover:bg-[#333] transition-colors"
          data-testid="home-cta-button"
        >
          {c.get('cta.button_label', 'Begin Your Experience')}
        </Link>
      </section>
    </div>
  );
}
